/**
 * PDF Watermark Service
 * Applies real watermarks to PDFs using backend proxy to avoid CORS
 */

import { PDFDocument, rgb, degrees, StandardFonts } from 'pdf-lib';
import api from '../utils/api';

class PDFWatermarkService {
  /**
   * Generate watermark info
   */
  generateWatermarkInfo(userInfo, dataroomId = null) {
    return {
      userName: `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim() || 'User',
      userEmail: userInfo?.email || 'user@example.com',
      timestamp: new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }),
      dataroomId: dataroomId || 'DATAROOM'
    };
  }

  /**
   * Apply watermark to PDF bytes
   */
  async applyWatermarkToPDF(pdfArrayBuffer, watermarkInfo) {
    try {
      
      const pdfDoc = await PDFDocument.load(pdfArrayBuffer, {
        ignoreEncryption: true
      });
      
      // Embed fonts
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      
      const pages = pdfDoc.getPages();
      
      // Format watermark text
      const userName = watermarkInfo.userName || 'Authorized User';
      const userEmail = watermarkInfo.userEmail || 'user@example.com';
      const timestamp = watermarkInfo.timestamp || new Date().toISOString();
      const dataroomId = watermarkInfo.dataroomId || 'DATAROOM';
      
      // Watermark pattern text
      const watermarkText = `${userName} • ${userEmail}`;
      const secondaryText = `${timestamp} • DR: ${dataroomId}`;
      
      pages.forEach((page, pageIndex) => {
        const { width, height } = page.getSize();
        
        // Simplified watermark - only user info and current timestamp
        const userName = watermarkInfo.userName || 'User';
        const userEmail = watermarkInfo.userEmail || 'user@example.com';
        const currentDateTime = new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
        
        // Single watermark line
        const watermarkLine = `${userName} • ${userEmail} • ${currentDateTime}`;
        
        // Pattern configuration - bottom-left to top-right
        const horizontalSpacing = 250;
        const verticalSpacing = 100;
        const rotation = 45; // Positive for bottom-left to top-right
        
        // Calculate grid
        const cols = Math.ceil(width / horizontalSpacing) + 2;
        const rows = Math.ceil(height / verticalSpacing) + 2;
        
        // Draw diagonal pattern from bottom-left to top-right
        for (let row = -1; row < rows; row++) {
          for (let col = -1; col < cols; col++) {
            const x = col * horizontalSpacing;
            const y = row * verticalSpacing;
            
            // Draw watermark text
            page.drawText(watermarkLine, {
              x: x,
              y: y,
              size: 11,
              font: regularFont,
              color: rgb(0.4, 0.4, 0.4),
              opacity: 0.2,
              rotate: degrees(rotation)
            });
          }
        }
      });
      
      const pdfBytes = await pdfDoc.save();
      return pdfBytes;
      
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch PDF from backend and apply watermark
   */
  async fetchAndWatermarkPDF(dataroomUuid, fileUuid, watermarkInfo) {
    try {
      // First, try to get the file info with public URL
      try {
        const viewResponse = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/view`, {
          headers: {
            'Accept': 'application/json'
          }
        });
        
        
        // Get the public URL from response
        const fileData = viewResponse.data.file || viewResponse.data;
        const publicUrl = fileData.public_url;
        
        if (publicUrl) {
          
          // Fetch PDF directly from public URL
          const pdfResponse = await fetch(publicUrl, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit' // Don't send credentials to S3
          });
          
          if (pdfResponse.ok) {
            const blob = await pdfResponse.blob();
            const arrayBuffer = await blob.arrayBuffer();
            
            
            // Apply watermark
            const watermarkedPdfBytes = await this.applyWatermarkToPDF(arrayBuffer, watermarkInfo);
            
            // Convert back to blob
            const watermarkedBlob = new Blob([watermarkedPdfBytes], { type: 'application/pdf' });
            
            // Create object URL for display
            const url = URL.createObjectURL(watermarkedBlob);
            
            
            return {
              url: url,
              blob: watermarkedBlob,
              isWatermarked: true
            };
          }
        }
      } catch (fetchError) {
      }
      
      // Fallback: Try download endpoint which might proxy the file
      const response = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/download`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      
      // Convert blob to array buffer
      const arrayBuffer = await response.data.arrayBuffer();
      
      // Apply watermark
      const watermarkedPdfBytes = await this.applyWatermarkToPDF(arrayBuffer, watermarkInfo);
      
      // Convert back to blob
      const watermarkedBlob = new Blob([watermarkedPdfBytes], { type: 'application/pdf' });
      
      // Create object URL for display
      const url = URL.createObjectURL(watermarkedBlob);
      
      return {
        url: url,
        blob: watermarkedBlob,
        isWatermarked: true
      };
      
    } catch (error) {
      
      // Try alternative view endpoint
      try {
        const viewResponse = await api.get(`/datarooms/${dataroomUuid}/files/${fileUuid}/view`, {
          headers: {
            'Accept': 'application/json'
          }
        });
        
        // Get proxy URL from response
        const fileData = viewResponse.data.file || viewResponse.data;
        const proxyUrl = fileData.proxy_url || fileData.proxy_download_url;
        
        if (proxyUrl) {
          
          // Fetch from proxy URL
          const proxyResponse = await fetch(proxyUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Accept': 'application/pdf'
            }
          });
          
          if (proxyResponse.ok) {
            const blob = await proxyResponse.blob();
            const arrayBuffer = await blob.arrayBuffer();
            
            // Apply watermark
            const watermarkedPdfBytes = await this.applyWatermarkToPDF(arrayBuffer, watermarkInfo);
            const watermarkedBlob = new Blob([watermarkedPdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(watermarkedBlob);
            
            return {
              url: url,
              blob: watermarkedBlob,
              isWatermarked: true
            };
          }
        }
      } catch (viewError) {
      }
      
      throw error;
    }
  }

  /**
   * Process PDF for preview with real watermark
   */
  async processPDFForPreview(dataroomUuid, file, userInfo) {
    try {
      const fileUuid = file.uuid || file.id;
      const watermarkInfo = this.generateWatermarkInfo(userInfo, dataroomUuid);
      
      // Fetch and watermark PDF through backend
      const result = await this.fetchAndWatermarkPDF(dataroomUuid, fileUuid, watermarkInfo);
      
      return {
        ...result,
        watermarkInfo: watermarkInfo,
        fileName: file.name || file.display_name || 'document.pdf'
      };
      
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process PDF for download with real watermark
   */
  async processPDFForDownload(dataroomUuid, file, userInfo, watermarkEnabled = true) {
    try {
      const fileUuid = file.uuid || file.id;
      const watermarkInfo = this.generateWatermarkInfo(userInfo, dataroomUuid);
      
      // If watermark is disabled, fetch original PDF without watermarking
      if (!watermarkEnabled) {
        // Fetch original PDF through backend without watermark
        const response = await api.get(`/dataroom/${dataroomUuid}/file/${fileUuid}/download`, {
          responseType: 'blob'
        });
        
        return {
          blob: response.data,
          fileName: file.name || file.display_name || 'document.pdf',
          isWatermarked: false,
          watermarkInfo: null
        };
      }
      
      // Fetch and watermark PDF through backend
      const result = await this.fetchAndWatermarkPDF(dataroomUuid, fileUuid, watermarkInfo);
      
      return {
        blob: result.blob,
        fileName: file.name || file.display_name || 'document.pdf',
        isWatermarked: true,
        watermarkInfo: watermarkInfo
      };
      
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
const pdfWatermarkService = new PDFWatermarkService();
export default pdfWatermarkService;